import { shallowEqual, type TypedUseSelectorHook, useDispatch as useAppDispatch, useSelector as useAppSelector } from 'react-redux';
import context from '@alltrails/context/slices/contextSlice';
import currencyCode from '@alltrails/context/slices/currencyCodeSlice';
import plans from '@alltrails/context/slices/plansSlice';
import type Context from '@alltrails/core/types/Context';
import features from '@alltrails/features/slices/features';
import amplitudeAnalyticsSettings from '@alltrails/amplitude/slices/amplitudeAnalyticsSettings';
import { configureStore } from '@reduxjs/toolkit';
import { experimentsReducer as experiments } from '@alltrails/experiments';
import { baseApi } from '@alltrails/redux-helpers';
import algoliaConfigs from '@alltrails/redux/slices/algoliaConfigs';
import lists, { type ListsState } from '@alltrails/redux/slices/lists';
import { MapState, mapReducer as map } from '@alltrails/maps';
import { AlgoliaConfigs } from '@alltrails/types/algoliaConfigs';
import type CountryCode from '@alltrails/core/types/CountryCode';
import type RequiredPreloadedState from '@alltrails/core/types/RequiredPreloadedState';

export type PreloadedState = RequiredPreloadedState &
  Partial<{
    algoliaConfigs: AlgoliaConfigs;
    lists: ListsState;
    map: MapState;
  }>;

export const reducer = {
  algoliaConfigs,
  amplitudeAnalyticsSettings,
  [baseApi.reducerPath]: baseApi.reducer,
  browser: (state = {}) => state,
  context,
  currencyCode,
  experiments,
  features,
  irclickid: (state = '') => state,
  lists,
  map,
  plans,
  requestCountryCode: (state = '' as CountryCode) => state
};

export const buildStore = (preloadedState?: PreloadedState) =>
  configureStore({
    reducer,
    preloadedState,
    middleware: getDefaultMiddleware => getDefaultMiddleware({ immutableCheck: false }).concat(baseApi.middleware)
  });

export type AppStore = ReturnType<typeof buildStore>;
export type AppDispatch = AppStore['dispatch'];
export type RootState = ReturnType<AppStore['getState']> & { context?: Context };

// these are intended to use within the app only
export const useDispatch = (): AppDispatch => useAppDispatch<AppDispatch>();
export const useSelector: TypedUseSelectorHook<RootState> = (selector: (state: RootState) => any, equalityFn = shallowEqual) =>
  useAppSelector<RootState, any>(selector, equalityFn);
